import { loadWasm } from 'necjs';
import config from '../web3/web3Config.json';
import { mintDekHex, stripNul } from './dekManager.js';

/**
 * envelopeV3.js — dMail v3 recipient envelope.
 *
 * WHY THIS EXISTS
 * ---------------
 * v1 encrypts the ENTIRE payload directly to each recipient's public key, once per recipient per item
 * (body, and every attachment separately). On receive, that whole ciphertext has to go through the wallet's
 * asymmetric unwrap. On the web that is survivable (window.ncogProvider is in-page postMessage with no size
 * ceiling); on Android the wallet is reached through a deep link and the ciphertext travels in an Intent
 * DATA URI across a 1 MB Binder buffer, so a 371 KB attachment (677,896 chars ~= 1.48 MB parcelled) cannot
 * succeed — it presents as a ~130 s freeze and then a failure.
 *
 * v3 keeps the same primitives and only changes WHAT the wallet is asked to open:
 *
 *     encryptedData = wrapK(recipientPub, K)  ||  symSeal(K, plaintext)
 *                     `- exactly 2596 chars -'  `----- remainder -----'
 *     version       = "v3"
 *
 * K is a per-envelope content key. Both the wrap and the seal happen LOCALLY in WASM — public-key
 * encryption needs no private key, so nothing here touches the wallet. On receive the wallet opens only the
 * 2596-char wrapped K (~5.2 KB parcelled, ~261x smaller than v1), and the body and attachments are then
 * opened locally with K.
 *
 * The 2596-char unwrap is not a new shape: dekManager already wraps a 64-hex-char DEK with the same
 * Mlkem.encrypt and passes the identical 2596-char result through ncog_decrypt in production today. No
 * wallet change is required.
 *
 * INVARIANTS — violating any of these silently corrupts mail
 * ----------------------------------------------------------
 *  - PLAIN CONCATENATION, no delimiter, no prefix, no JSON, and NO SIBLING FIELD. The server persists only
 *    `encryptedData` and `version` out of the posted `msg` (DEmailsService.GetEncryptValueAsync) and the
 *    attachment reader collapses every parsed blob to { encryptedData: json.data, version: json.version },
 *    so a third field carrying the wrapped key is silently dropped and the mail is unopenable forever.
 *  - The split is by FIXED OFFSET, and the offset is a shared constant. `wrapped.length === 2596` is
 *    asserted on write and the blob length is asserted on read. Never truncate, never pad, never inline the
 *    number at a call site. The length is deterministic (gzip(64 float32) || ML-KEM-1024 ciphertext, then
 *    base64), but base64 quantisation only absorbs a +0/+2 byte drift, so the assert is the safety net.
 *  - symDecrypt's third argument is the CRYPTO version and is ALWAYS the literal 'v1'. The ENVELOPE version
 *    ('v3') and the crypto version are different things; the Go core hard-rejects anything but 'v1'.
 *  - K is a STRING of exactly 64 lowercase hex characters at every boundary — never bytes, never uppercase,
 *    never 0x-prefixed. The unwrap asserts /^[0-9a-f]{64}$/ before K is used for anything.
 *  - Dispatch on the version string, ALWAYS, before any wallet call. Never let the wallet decide: handing a
 *    legacy reader a v3 blob PANICS the Go runtime (core.go calls panic(err), not return err, on a base64
 *    decode failure), and a concatenation of two independently base64-encoded strings can never itself be
 *    valid base64 — so there is no wire layout that avoids this. Unknown/absent version => v1, never error.
 *  - v3 is RECIPIENT-BOUND DATA ONLY. The sender's own saved copy stays on the DEK ('v2') path, untouched:
 *    the DEK is per-user and cached across sessions, K is per-envelope and discarded, so moving Sent items
 *    onto K would make every existing Sent item unreadable.
 */

/** The envelope version tag written to `msg.version` and to the attachment JSON's `version` field. */
export const ENVELOPE_VERSION_V3 = 'v3';

/**
 * Length of the wrapped content key, in characters. Empirically 2596 in every observed sample:
 * 2472 (the fixed KEM header the Go core itself hard-slices at) + ceil((64 + 28) / 3) * 4 = 2472 + 124.
 * This is the ONE place the number lives.
 */
export const WRAPPED_K_LEN = 2596;

/**
 * Smallest possible v3 blob: a wrapped K plus the seal of an empty plaintext
 * (2596 + 2472 + ceil(28 / 3) * 4 = 2596 + 2512). Anything shorter is not a v3 blob, and slicing it would
 * throw out of the Go runtime rather than return an error.
 */
export const MIN_V3_BLOB_LEN = 5108;

/** ML-KEM-1024 public key: 1568 bytes rendered as hex. */
export const PUBLIC_KEY_HEX_LEN = 3136;

const K_HEX = /^[0-9a-f]{64}$/;

/**
 * Every failure in this module is an EnvelopeV3Error.
 *
 * It exists so the send path can tell a v3 failure apart from anything else and ABORT. The v3 path must
 * never fall back to v1, to the DEK, or to plaintext: a v3 blob written with the wrong tag, or a v1 blob
 * written where a v3 one was promised, is unreadable forever and can crash the reader's wallet.
 */
export class EnvelopeV3Error extends Error {
  constructor(message) {
    super(message);
    this.name = 'EnvelopeV3Error';
  }
}

/**
 * Normalise a version marker for dispatch: String(x ?? 'v1').toLowerCase().
 *
 * Unknown, absent, null and unrecognised all mean 'v1' — web2/Gmail-origin mail and everything predating
 * the DEK rollout arrives with no version at all, and the attachment reader has always defaulted that way.
 */
export function normalizeVersion(value) {
  return String(value ?? 'v1').toLowerCase();
}

/** True when this version marker selects the v3 split path. Nothing else may split a blob. */
export function isV3Version(value) {
  return normalizeVersion(value) === ENVELOPE_VERSION_V3;
}

/**
 * v3 is now the ONLY recipient-bound write format. Always true.
 *
 * This was a DMAIL_V3_WRITE flag, staged off so no v3 blob could reach a pre-v3 reader — such a blob does
 * not fail cleanly, it panics the Go crypto runtime. Removed deliberately: this deployment has no users
 * yet and its existing mail is being cleared, so there is nothing to stage for. A flag whose only setting
 * is "on" implies a rollback that does not work, because v1 and v3 blobs are not interchangeable once
 * written.
 *
 * The ordering it encoded is now a DEPLOY-ORDER requirement: DSuite.Emails.Api ships FIRST. Its
 * GetEncryptedMessageAsync / GetSecureEmailAsync passthrough must accept 'v3', or mobile renders every
 * v3 body as a silent empty message.
 *
 * Must stay in lockstep with the mobile isV3WriteEnabled() in helpers/envelope.ts — the two clients write
 * the same wire format and neither may unilaterally revert.
 */
export function isV3WriteEnabled() {
  return true;
}

function assertK(kHex, where) {
  if (typeof kHex !== 'string' || !K_HEX.test(kHex)) {
    // Never log or interpolate the key itself — only its shape.
    throw new EnvelopeV3Error(
      `envelopeV3(${where}): content key must be 64 lowercase hex chars (got ${typeof kHex === 'string' ? `${kHex.length} chars` : typeof kHex})`,
    );
  }
}

/**
 * The key is passed to the WASM exactly as getPublicKeyOfUser() returned it — bare hex, no 0x prefix, no
 * normalisation. That is what the v1 path has always done, and the Go layer rejects a 0x-prefixed key with
 * "invalid pubKey encoding" (verified), so normalising here would make v3 accept keys v1 refuses and put the
 * two paths on different inputs.
 */
function assertPublicKey(publicKey) {
  if (typeof publicKey !== 'string' || publicKey.length !== PUBLIC_KEY_HEX_LEN || !/^[0-9a-fA-F]+$/.test(publicKey)) {
    throw new EnvelopeV3Error(
      `envelopeV3(wrapK): recipient public key must be ${PUBLIC_KEY_HEX_LEN} bare hex chars (got ${typeof publicKey === 'string' ? `${publicKey.length} chars` : typeof publicKey})`,
    );
  }
}

/**
 * Mint a fresh content key: 32 random bytes as 64 lowercase hex chars.
 *
 * Same primitive as the DEK (dekManager.mintDekHex) but a completely different key LIFETIME: a DEK is
 * per-user and cached for the session, K is per-envelope and discarded the moment the send completes. K is
 * never persisted, never logged and never sent anywhere except wrapped to a recipient's public key.
 */
export function mintK() {
  const k = mintDekHex();
  assertK(k, 'mintK');
  return k;
}

/**
 * Wrap K to a recipient's chain public key. Local WASM, no wallet — public-key encryption needs no private
 * key. Returns EXACTLY WRAPPED_K_LEN characters or throws; it never truncates and never pads.
 *
 * The field name is normalised because the two crypto layers disagree: the native mobile bridge returns
 * { encrypted, version } and necjs returns { encryptedData, version }.
 */
export async function wrapK(publicKey, kHex) {
  assertK(kHex, 'wrapK');
  assertPublicKey(publicKey);
  let out;
  try {
    const Mlkem = await loadWasm();
    out = await Mlkem.encrypt(publicKey, kHex);
  } catch (err) {
    throw new EnvelopeV3Error(`envelopeV3(wrapK): ${err?.message || err}`);
  }
  const wrapped = typeof out === 'string' ? out : (out?.encrypted ?? out?.encryptedData);
  if (typeof wrapped !== 'string' || wrapped.length !== WRAPPED_K_LEN) {
    throw new EnvelopeV3Error(
      `envelopeV3(wrapK): wrapped key must be ${WRAPPED_K_LEN} chars, got ${typeof wrapped === 'string' ? wrapped.length : typeof wrapped}`,
    );
  }
  return wrapped;
}

/**
 * Local symmetric seal under K. Deliberately NOT dekManager.symEncrypt: that name says DEK, and nothing
 * recipient-bound may ever be sealed with the DEK. Same WASM primitive, different key.
 */
async function symSeal(kHex, plaintext) {
  try {
    const Mlkem = await loadWasm();
    const out = await Mlkem.symEncrypt(kHex, plaintext); // { encryptedData, version:'v1' }
    return out?.encryptedData ?? out;
  } catch (err) {
    throw new EnvelopeV3Error(`envelopeV3(symSeal): ${err?.message || err}`);
  }
}

/** Local symmetric open under K. The third argument is the CRYPTO version and is ALWAYS 'v1'. */
async function symOpen(kHex, ciphertext) {
  try {
    const Mlkem = await loadWasm();
    return await Mlkem.symDecrypt(kHex, ciphertext, 'v1');
  } catch (err) {
    throw new EnvelopeV3Error(`envelopeV3(symOpen): ${err?.message || err}`);
  }
}

/**
 * Build one v3 blob: the already-wrapped K, then the plaintext sealed under K, concatenated.
 *
 * `wrapped` is passed in rather than derived so that one recipient's body and ALL of that recipient's
 * attachments carry the identical prefix from a single KEM operation.
 */
export async function sealV3(wrapped, kHex, plaintext) {
  assertK(kHex, 'sealV3');
  if (typeof wrapped !== 'string' || wrapped.length !== WRAPPED_K_LEN) {
    throw new EnvelopeV3Error(
      `envelopeV3(sealV3): wrapped key must be ${WRAPPED_K_LEN} chars, got ${typeof wrapped === 'string' ? wrapped.length : typeof wrapped}`,
    );
  }
  const sealed = await symSeal(kHex, typeof plaintext === 'string' ? plaintext : String(plaintext ?? ''));
  if (typeof sealed !== 'string' || !sealed.length) {
    throw new EnvelopeV3Error('envelopeV3(sealV3): symmetric seal returned nothing');
  }
  const blob = wrapped + sealed;
  if (blob.length < MIN_V3_BLOB_LEN) {
    throw new EnvelopeV3Error(`envelopeV3(sealV3): blob is ${blob.length} chars, below the ${MIN_V3_BLOB_LEN} minimum`);
  }
  return blob;
}

/**
 * Split a v3 blob at the fixed offset. Throws before slicing if the blob is too short — a short slice would
 * otherwise reach the Go runtime and panic there ("slice bounds out of range") rather than return an error.
 */
export function splitV3(blob) {
  const s = typeof blob === 'string' ? blob : String(blob ?? '');
  if (s.length < MIN_V3_BLOB_LEN) {
    throw new EnvelopeV3Error(
      `envelopeV3(splitV3): blob is ${s.length} chars, below the ${MIN_V3_BLOB_LEN} minimum for v3 — refusing to split`,
    );
  }
  return { wrapped: s.slice(0, WRAPPED_K_LEN), sealed: s.slice(WRAPPED_K_LEN) };
}

/* ------------------------------------------------------------------ *
 * Unwrapped content-key cache
 * ------------------------------------------------------------------ */

/**
 * Unwrapped content keys, keyed on the EXACT wrapped blob that produced them.
 *
 * WHY THIS EXISTS — the user-visible failure it prevents
 * -----------------------------------------------------
 * One recipient copy has ONE K: the body blob and every attachment blob carry the byte-identical
 * 2596-char `wrapped` prefix (emailHelper mints one K per recipient and hands the same `wrapped` to
 * sealV3 for the body and to encryptFilesV3 for every file). Without memoisation, opening a mail with
 * attachments unwrapped that same key once per item: the body (Messages.js / Encrypt.js), then again
 * for each attachment (attachmentHelper multipleDecryption / getFile / downloadFile). Users saw two
 * back-to-back wallet prompts carrying the IDENTICAL ciphertext for a mail with one attachment, and one
 * more per extra file. Mobile has always cached this (helpers/envelope.ts); web did not.
 *
 * WHY THIS CANNOT DECRYPT ONE MESSAGE WITH ANOTHER'S KEY
 * -----------------------------------------------------
 * The map key is the WHOLE wrapped blob — all 2596 characters of ML-KEM ciphertext — not a message id,
 * not an s3url, not a prefix. So this is pure memoisation of a deterministic function: a hit can only
 * ever return the key that this very ciphertext unwraps to, which is exactly what the wallet would have
 * returned. Two different messages carry independently random K AND independent KEM encapsulation
 * randomness, so sharing an entry would require two distinct messages to have produced identical
 * 2596-char ciphertexts. A truncated or derived key (message id, sender, first N chars) would NOT be
 * safe and must never be substituted here.
 *
 * The one thing the blob alone does not encode is WHOSE wallet opened it, so the cache is additionally
 * scoped to the signed-in account and is dropped the moment that account changes — otherwise an entry
 * unwrapped by user A could be served to user B without a wallet call, handing B a key A's wallet holds
 * the private half of. clearEnvelopeKeyCache() below is called beside clearDekCache() at every sign-out
 * and account-switch site; the owner check is the defence-in-depth for a site added later that forgets.
 *
 * SESSION MEMORY ONLY — never localStorage, never IndexedDB, never a cookie. A persisted content key
 * outlives the session and is a plaintext key sitting next to the ciphertext it opens. Bounded so a long
 * session cannot accumulate keys for a whole mailbox.
 */
const K_CACHE_LIMIT = 8;
const _kCache = new Map();
/** In-flight unwraps, so concurrent openers of one message share ONE wallet round-trip (see unwrapK). */
const _kInflight = new Map();
let _kCacheUser = null;

/** The account the cache belongs to. Same source of truth as dekManager's currentUser(). */
function cacheOwner() {
  try {
    return localStorage.getItem('UserName') || '';
  } catch {
    return '';
  }
}

/**
 * Drop every cached content key. Call on sign-out and on account switch, beside clearDekCache() —
 * mirrors mobile's clearEnvelopeKeyCache().
 */
export function clearEnvelopeKeyCache() {
  _kCache.clear();
  _kInflight.clear();
  _kCacheUser = null;
}

/** Wipe the cache if the signed-in account changed since it was populated. */
function syncCacheOwner() {
  const user = cacheOwner();
  if (_kCacheUser !== user) {
    _kCache.clear();
    _kInflight.clear();
    _kCacheUser = user;
  }
  return user;
}

/**
 * Coerce whatever the wallet handed back into K.
 *
 * Two in-tree shapes disagree about this and both are handled: some paths return the bare plaintext, others
 * return an envelope {"decryptedData":"…","version":"v1"}. The regex assertion afterwards is what makes
 * accepting both safe — anything that is not exactly 64 lowercase hex chars is rejected before it can be
 * used as a key.
 */
function coerceK(raw) {
  let value = stripNul(raw);
  if (value.startsWith('{')) {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed.decryptedData === 'string') {
        value = stripNul(parsed.decryptedData);
      }
    } catch {
      /* not an envelope — fall through to the assertion below */
    }
  }
  assertK(value, 'unwrapK');
  return value;
}

/**
 * Unwrap K inside the wallet. THIS IS THE ONLY WALLET ROUND-TRIP IN THE v3 READ PATH, and it carries
 * exactly WRAPPED_K_LEN characters. The RPC parameter is `cypherText` (suite-wide), and the version handed
 * to the wallet is the CRYPTO version 'v1' — never 'v3'.
 *
 * At most ONE round-trip per (account, wrapped key). The body and every attachment of a message share one
 * `wrapped`, so the first opener pays for the prompt and the rest are served from memory — see the cache
 * note above for why a hit can only ever return this exact blob's own key.
 */
export async function unwrapK(wrapped) {
  if (typeof wrapped !== 'string' || wrapped.length !== WRAPPED_K_LEN) {
    throw new EnvelopeV3Error(
      `envelopeV3(unwrapK): expected ${WRAPPED_K_LEN} chars, got ${typeof wrapped === 'string' ? wrapped.length : typeof wrapped}`,
    );
  }

  const owner = syncCacheOwner();
  const cached = _kCache.get(wrapped);
  if (cached) return cached;
  // Attachments are opened CONCURRENTLY in places (common-alert.js setInitialFiles/getfile and
  // Encrypt.js getDecryptedfile both Promise.all over getFile), so a plain read-through cache would
  // still fire one prompt per file — they all miss before any of them resolves. Joining the in-flight
  // request is what collapses that to one.
  const pending = _kInflight.get(wrapped);
  if (pending) return await pending;

  const request = (async () => {
    if (!window?.ncogProvider?.request) {
      throw new EnvelopeV3Error('envelopeV3(unwrapK): no NCOG wallet provider available');
    }
    const res = await window.ncogProvider.request({
      method: 'ncog_decrypt',
      params: { cypherText: wrapped, version: 'v1' },
    });
    if (!res || res.success === false || !res.response) {
      throw new EnvelopeV3Error('envelopeV3(unwrapK): ncog_decrypt refused the wrapped content key');
    }
    return coerceK(res.response);
  })();

  _kInflight.set(wrapped, request);
  try {
    const k = await request;
    // Memoise only if the signed-in account is still the one that asked. An account switch while the
    // wallet prompt was open must not leave the previous user's content key where the new user's reads
    // would find it. A failed unwrap is never cached — the `finally` below is the only bookkeeping, so
    // the next attempt goes back to the wallet.
    if (cacheOwner() === owner) {
      if (_kCache.size >= K_CACHE_LIMIT) {
        const oldest = _kCache.keys().next().value;
        if (oldest !== undefined) _kCache.delete(oldest);
      }
      _kCache.set(wrapped, k);
    }
    return k;
  } finally {
    _kInflight.delete(wrapped);
  }
}

/**
 * Open a v3 blob end to end: split, unwrap K in the wallet, then decrypt the remainder locally.
 *
 * Callers MUST have dispatched on the version marker first (isV3Version) — this function does not sniff and
 * must never be handed a v1 or v2 blob.
 */
export async function openV3(blob) {
  const { wrapped, sealed } = splitV3(blob);
  const k = await unwrapK(wrapped);
  return stripNul(await symOpen(k, sealed));
}
